import { TFunction } from "i18next";
import * as yup from "yup";

import { ProgramType } from "../Program/ProgramType";
import { OtherEnrollmentConditionId } from "./EnrollmentCondition"
import { OtherLegalEntityLocationValidationScheme } from "./OtherLegalEntityLocationValidationScheme";
import { TeacherValidationScheme } from "./TeacherValidationScheme";


export interface ProgramRequestDetailValidationSchemeProps { t: TFunction; }
export const ProgramRequestDetailValidationScheme = ({ t }: ProgramRequestDetailValidationSchemeProps) => {
  const requiredMsg = t("validations.required");
  const requiredAtLeastOne = t("validations.requiredAtLeastOne");
  const maximumTenDocuments = t("validations.maximumTenDocuments");
  const maximumTenOtherLegalEntityLocations = t("validations.maximumTenOtherLegalEntityLocations");
  const requiredAtLeastOneTeacher = t("validations.requiredAtLeastOneTeacher");

  const executionResolutionDocumentMinDate = new Date(2022, 0, 1);
  const expertOpinionDocumentMinDateMsg = t("request.expertOpinionDocumentMinDate");
  const levelValidation = t("request.levelValidation");
  const csvetValidation = t("request.csvetValidation");
  const remarkValidation = t("request.remarkValidation");

  const executionResolutionDocumentMaxDate = (): Date => {
    const now = new Date();
    const todayLastSecond = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return todayLastSecond;
  };
  const executionResolutionDocumentMaxDateMsg = t("request.executionResolutionDocumentMaxDate");
  const positiveNumberOrZeroMsg = t("validations.positiveNumberOrZero");
  const numberLessThanTousend = t("validations.numberLessThan", { nr: 6000 });

  return yup.object().shape({
    // hkoLevelId: yup.number().when("programTypeId", {
    //     is: ProgramType.TrainingPrograms,
    //     then: yup.number().required(requiredMsg).min(1, requiredMsg),
    //     otherwise: yup.number()
    //   }),
    qualificationTypeId: yup.number().when("programId", {
      is: -1,
      then: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).typeError(requiredMsg),
      otherwise: yup.number().nullable()
    }),
    educationSubSectorId: yup.number().nullable().when("programId", {
      is: -1,
      then: yup.number().nullable().required(requiredMsg),
      otherwise: yup.number().nullable()
    }),
    curriculumProgramDocument: yup.object().nullable().required(requiredMsg),
    guidedLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    independentLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    workLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    formOfTeachingIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    knowledgeChecksIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    enrollmentConditionsIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    otherEnrollmentConditions: yup.array().of(yup.string()).when("enrollmentConditionsIds", (enrollmentConditionsIds, schema) => {
      if (enrollmentConditionsIds && enrollmentConditionsIds.includes(OtherEnrollmentConditionId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      } else {
        return schema;
      }
    }),
    programName: yup.string().when("programId", {
      is: -1,
      then: yup.string().nullable().required(requiredMsg),
      otherwise: yup.string().nullable()
    }),
    remarkInstitution: yup.string().nullable().notRequired().test('remarkInstitution', remarkValidation, val => {
      if (val) {
        console.log('remarkInstitution ' + val.length);
        return val.length < 701;
      }
      return true;
    }),
    languageLevelGeneralIds: yup.array().of(yup.number()),
    languageLevelBusinessIds: yup.array().of(yup.number()),
    programRequestTeachers: yup.array().of(
      TeacherValidationScheme({ t })
    ).min(1, requiredAtLeastOneTeacher),
    competences: yup.array().of(
      yup.object().nullable().when("programTypeId", (programTypeId, schema) => {
        if (programTypeId === ProgramType.BasicEducationPrograms ||
          programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
          programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
          programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
          programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
          programTypeId === ProgramType.BasicArtsPrograms ||
          programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
          programTypeId === ProgramType.CroatianLanguageForForeigners) {
          return schema;
        } else {
          return yup.object().shape({ name: yup.string().trim().required(requiredMsg).max(512, t("validations.maximalStringLength", { maxLength: 512 })) });
        }
      })
    ),
    otherLegalEntityLocations: yup.array().when("locationOtherLegalEntity", {
      is: true,
      then: yup.array().of(
        OtherLegalEntityLocationValidationScheme({ t })
      ).min(1, requiredAtLeastOne).max(10, maximumTenOtherLegalEntityLocations),
      otherwise: yup.array()
    }),
  });
};
